'use client';
import React, { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, Upload, Check, AlertTriangle, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { US_STATES, KENYAN_COUNTIES, RELATIONSHIP_INTENTIONS, FAITH_OPTIONS, INTERESTS_LIST } from '@/lib/data';
import { jsnService } from '@/lib/supabase/jsnService';
import PhoneVerificationScreen from './PhoneVerificationScreen';
import PhotoCropModal from '@/components/PhotoCropModal';

interface RegisterFormData {
  firstName: string;
  lastName: string; // stores the single last-name initial
  email: string;
  password: string;
  confirmPassword: string;
  dateOfBirth: string;
  gender: string;
  usState: string;
  kenyanCounty: string;
  intention: string;
  faith: string;
  bio: string;
  phoneNumber: string;
  agreeTerms: boolean;
  agreeSmsConsent: boolean;
}

interface RegisterFormProps {
  onSwitchToLogin: () => void;
  onRegistrationComplete?: () => void;
}

function FieldWarning({ message }: { message: string }) {
  return (
    <p className="flex items-center gap-1.5 text-amber-500 text-xs mt-1.5 font-medium">
      <AlertTriangle size={12} className="flex-shrink-0" />
      {message}
    </p>
  );
}

// Validate US phone number using libphonenumber-js
function validateUSPhone(value: string): string | true {
  if (!value || value.trim() === '') return 'Phone number is required.';
  const digits = value.replace(/\D/g, '');
  // Must be exactly 10 digits (after stripping non-digits, excluding leading 1)
  const tenDigits = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (tenDigits.length !== 10) return 'Please enter a valid U.S. phone number.';
  // Area code and central office code cannot start with 0 or 1
  if (/^[01]/.test(tenDigits)) return 'Please enter a valid U.S. phone number.';
  if (/^.{3}[01]/.test(tenDigits)) return 'Please enter a valid U.S. phone number.';
  try {
    const e164 = `+1${tenDigits}`;
    if (!isValidPhoneNumber(e164, 'US')) {
      return 'JSN currently accepts U.S.-based members only. Please use a valid +1 phone number.';
    }
    return true;
  } catch {
    return 'Please enter a valid U.S. phone number.';
  }
}

// Convert input to E.164 format
function toE164(value: string): string {
  const digits = value.replace(/\D/g, '');
  const tenDigits = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  return `+1${tenDigits}`;
}

export default function RegisterForm({ onSwitchToLogin, onRegistrationComplete }: RegisterFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [interestsError, setInterestsError] = useState<string | null>(null);
  const [showVerifyNotice, setShowVerifyNotice] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [bioLength, setBioLength] = useState(0);

  // Phone verification state — kept for Phase 2 re-enablement
  // Phase 1: OTP screen is disabled; phone is saved with phone_verified=false
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [pendingPhoneE164, setPendingPhoneE164] = useState('');
  const [pendingUserId, setPendingUserId] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);

  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);

  const photoSectionRef = useRef<HTMLDivElement>(null);
  const interestsSectionRef = useRef<HTMLDivElement>(null);
  const firstErrorRef = useRef<HTMLDivElement | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RegisterFormData>();

  const passwordValue = watch('password');

  const toggleInterest = (interest: string) => {
    setSelectedInterests(prev =>
      prev.includes(interest) ? prev.filter(i => i !== interest) : [...prev, interest]
    );
    if (interestsError) setInterestsError(null);
  };

  const onSubmit = async (data: RegisterFormData) => {
    if (!photoPreview) {
      setPhotoError('Please upload a clear profile photo to continue.');
      photoSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setPhotoError(null);

    if (selectedInterests.length === 0) {
      setInterestsError('Please select at least one interest.');
      interestsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setInterestsError(null);

    setIsLoading(true);

    try {
      const phoneE164 = toE164(data.phoneNumber);
      // Ensure last name initial is stored as uppercase single letter
      const lastNameInitial = data.lastName.trim().toUpperCase().charAt(0);

      const result = await jsnService.registerUser({
        email: data.email,
        password: data.password,
        firstName: data.firstName.trim(),
        lastName: lastNameInitial,
        gender: data.gender,
        usState: data.usState,
        kenyanCounty: data.kenyanCounty,
        intention: data.intention,
        faith: data.faith,
        bio: data.bio,
        dateOfBirth: data.dateOfBirth,
        interests: selectedInterests,
        photoUrl: photoPreview,
        phoneNumber: phoneE164,
        smsConsent: data.agreeSmsConsent === true,
      });

      if (result.success && result.userId) {
        // Phase 1: Skip SMS OTP verification screen.
        // Phone number is already saved by registerUser with phone_verified=false.
        // OTP enforcement will be re-enabled in Phase 2 after SMS provider is configured.
        // To re-enable: uncomment the lines below and remove the direct flow lines.
        //
        // setPendingPhoneE164(phoneE164);
        // setPendingUserId(result.userId);
        // setShowPhoneVerification(true);

        // Phase 1 direct flow: show success modal before switching to sign in
        setShowSuccessModal(true);
      } else {
        toast.error(result.error || 'Registration failed. Please try again.');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const onInvalid = () => {
    if (!photoPreview) {
      setPhotoError('Please upload a clear profile photo to continue.');
      photoSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (firstErrorRef.current) {
      firstErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setCropImageSrc(ev.target?.result as string);
        setPhotoError(null);
      };
      reader.readAsDataURL(file);
      e.target.value = '';
    }
  };

  const handleCropComplete = (croppedDataUrl: string) => {
    setPhotoPreview(croppedDataUrl);
    setPhotoError(null);
    setCropImageSrc(null);
  };

  const handleCropCancel = () => {
    setCropImageSrc(null);
  };

  const handlePhoneVerified = () => {
    setPhoneVerified(true);
    setShowPhoneVerification(false);
    setShowVerifyNotice(true);
    onRegistrationComplete?.();
  };

  const selectClass = `input-jsn appearance-none cursor-pointer`;

  // ── Phone OTP verification step (Phase 1: disabled — re-enable for Phase 2) ──
  // if (showPhoneVerification && pendingUserId) {
  //   return (
  //     <PhoneVerificationScreen
  //       phoneNumber={pendingPhoneE164}
  //       userId={pendingUserId}
  //       onVerified={handlePhoneVerified}
  //       onBack={() => setShowPhoneVerification(false)}
  //     />
  //   );
  // }

  // ── Success modal after account creation ──────────────────────────────────
  if (showSuccessModal) {
    return (
      <div className="fade-in space-y-5">
        <div className="bg-card border border-border rounded-2xl p-8 text-center space-y-5">
          <div className="w-16 h-16 rounded-full bg-primary/15 flex items-center justify-center mx-auto">
            <Check size={28} className="text-primary" />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-bold text-foreground">Welcome to Jamuhuri Singles Network</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Your account has been created successfully.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Please sign in to continue setting up your JSN profile.
            </p>
          </div>
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 text-left space-y-1">
            <p className="text-xs font-semibold text-amber-600 dark:text-amber-400">What happens next?</p>
            <p className="text-xs text-muted-foreground">1. Verify your email via the link sent to you.</p>
            <p className="text-xs text-muted-foreground">2. Sign in to your new account.</p>
            <p className="text-xs text-muted-foreground">3. Your profile photo will be reviewed by our team.</p>
            <p className="text-xs text-muted-foreground">4. Once approved, you can browse Discover!</p>
          </div>
          <button
            onClick={() => {
              setShowSuccessModal(false);
              onSwitchToLogin();
            }}
            className="btn-primary w-full py-3 rounded-xl text-sm font-semibold"
          >
            Sign In to Continue
          </button>
        </div>
      </div>
    );
  }

  // ── Email verification notice (legacy — kept for phone-verified path) ──────
  if (showVerifyNotice) {
    return (
      <div className="fade-in space-y-5">
        <div className="bg-card border border-border rounded-2xl p-8 text-center space-y-5">
          <div className="w-16 h-16 rounded-full bg-primary/15 flex items-center justify-center mx-auto">
            <Check size={28} className="text-primary" />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-bold text-foreground">Check Your Email!</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              We sent a verification link to your email. Click the link to verify your account, then sign in.
            </p>
          </div>
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 text-left space-y-1">
            <p className="text-xs font-semibold text-amber-600 dark:text-amber-400">What happens next?</p>
            <p className="text-xs text-muted-foreground">1. Verify your email via the link sent to you.</p>
            <p className="text-xs text-muted-foreground">2. Sign in to your new account.</p>
            <p className="text-xs text-muted-foreground">3. Your profile photo will be reviewed by our team.</p>
            <p className="text-xs text-muted-foreground">4. Once approved, you can browse Discover!</p>
          </div>
          <button
            onClick={onSwitchToLogin}
            className="btn-primary w-full py-3 rounded-xl text-sm font-semibold"
          >
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }

  // ── Registration form ──────────────────────────────────────────────────────
  return (
    <div className="fade-in space-y-5">
      {cropImageSrc && (
        <PhotoCropModal
          imageSrc={cropImageSrc}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}
      <div>
        <h2 className="text-2xl font-bold text-foreground">Create Your Profile</h2>
        <p className="text-muted-foreground text-sm mt-1">Join JSN — free to browse member profiles, upgrade to message</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-4" noValidate>
        {/* Profile Photo */}
        <div ref={photoSectionRef}>
          <label className="label-jsn">
            Profile Photo <span className="text-primary">*</span>
          </label>

          {/* Photo Standards Notice */}
          <div className="mb-3 bg-blue-500/8 border border-blue-500/20 rounded-xl p-3 space-y-1.5">
            <p className="text-xs font-semibold text-blue-600 dark:text-blue-400 flex items-center gap-1.5">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              Profile Photo Standards
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Please upload a clear, recent, close-up photo showing your full face. Head-and-shoulders photos are strongly recommended.
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              No nude, partially nude, sexually suggestive, blurry, distant, group, or face-obscured photos. Photos that do not meet these minimum standards will not be approved.
            </p>
            <p className="text-xs text-muted-foreground italic">Tip: Choose a photo where your face is easy to see and centered in the frame.</p>
          </div>

          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-xl border-2 flex items-center justify-center overflow-hidden flex-shrink-0 transition-colors ${
              photoError ? 'border-amber-500 bg-amber-500/10' : photoPreview ? 'border-accent/50 bg-muted/40' : 'border-dashed border-[#3a3a3a] bg-muted/40'
            }`}>
              {photoPreview ? (
                <img src={photoPreview} alt="Profile photo preview" className="w-full h-full object-cover" />
              ) : (
                <Upload size={20} className={photoError ? 'text-amber-500' : 'text-muted-foreground'} />
              )}
            </div>
            <div className="flex-1">
              <input type="file" accept="image/*" id="photo-upload" className="hidden" onChange={handlePhotoChange} />
              <label htmlFor="photo-upload" className="btn-outline-red px-4 py-2 rounded-lg text-xs font-medium cursor-pointer inline-block">
                {photoPreview ? 'Change Photo' : 'Choose Photo'}
              </label>
              <p className="text-xs text-muted-foreground mt-1">JPG, PNG, WEBP up to 5MB. Required.</p>
            </div>
          </div>
          {photoError && <FieldWarning message={photoError} />}
        </div>

        {/* Display Name — replaced with First Name + Last Name */}
        <div
          className="grid grid-cols-2 gap-3"
          ref={errors.firstName ? (el) => { if (!firstErrorRef.current) firstErrorRef.current = el; } : undefined}
        >
          <div>
            <label className="label-jsn" htmlFor="reg-first-name">
              First Name <span className="text-primary">*</span>
            </label>
            <input
              id="reg-first-name"
              type="text"
              className="input-jsn"
              placeholder="e.g. Mary"
              {...register('firstName', {
                required: 'First name is required',
                minLength: { value: 2, message: 'First name must be at least 2 characters' },
              })}
            />
            {errors.firstName && <FieldWarning message={errors.firstName.message!} />}
          </div>
          <div ref={errors.lastName ? (el) => { if (!firstErrorRef.current) firstErrorRef.current = el; } : undefined}>
            <label className="label-jsn" htmlFor="reg-last-name">
              Last Name Initial <span className="text-primary">*</span>
            </label>
            <input
              id="reg-last-name"
              type="text"
              className="input-jsn uppercase"
              placeholder="e.g. W"
              maxLength={1}
              {...register('lastName', {
                required: 'Enter one letter for your last name initial.',
                validate: (val) => {
                  const trimmed = val.trim();
                  if (trimmed.length !== 1) return 'Enter one letter for your last name initial.';
                  if (!/^[A-Za-z]$/.test(trimmed)) return 'Enter one letter for your last name initial.';
                  return true;
                },
                onChange: (e) => {
                  // Auto-uppercase and strip non-alpha characters
                  const raw = e.target.value.replace(/[^A-Za-z]/g, '').toUpperCase().charAt(0);
                  setValue('lastName', raw, { shouldValidate: true });
                },
              })}
            />
            {errors.lastName && <FieldWarning message={errors.lastName.message!} />}
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="label-jsn" htmlFor="reg-email">Email Address</label>
          <input id="reg-email" type="email" className="input-jsn" placeholder="your@email.com"
            {...register('email', { required: 'Email is required', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' } })}
          />
          {errors.email && <FieldWarning message={errors.email.message!} />}
        </div>

        {/* Phone Number */}
        <div>
          <label className="label-jsn" htmlFor="reg-phone">
            U.S. Phone Number <span className="text-primary">*</span>
          </label>
          <p className="text-xs text-muted-foreground mb-1.5">
            JSN is for U.S.-based members. A valid +1 number is required for SMS verification.
          </p>
          <div className="flex gap-2">
            <div className="flex items-center px-3 py-2 bg-muted/30 border border-border rounded-lg text-sm text-muted-foreground font-medium flex-shrink-0 select-none">
              <Phone size={14} className="mr-1.5 text-muted-foreground" />
              +1
            </div>
            <input
              id="reg-phone"
              type="tel"
              className={`input-jsn flex-1 ${errors.phoneNumber ? 'border-amber-500 bg-amber-500/5' : ''}`}
              placeholder="(555) 555-5555"
              {...register('phoneNumber', {
                required: 'Phone number is required.',
                validate: validateUSPhone,
              })}
            />
          </div>
          {errors.phoneNumber && <FieldWarning message={errors.phoneNumber.message!} />}
        </div>

        {/* SMS Consent Checkbox — required for A2P 10DLC compliance */}
        <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-3">
          <div className="flex items-start gap-3">
            <input
              id="agree-sms-consent"
              type="checkbox"
              className="w-4 h-4 mt-0.5 rounded border-border accent-primary cursor-pointer flex-shrink-0"
              defaultChecked={false}
              {...register('agreeSmsConsent')}
            />
            <label htmlFor="agree-sms-consent" className="text-sm text-foreground cursor-pointer leading-relaxed">
              I agree to receive one-time security verification codes from Jamuhuri Singles Network at the mobile number provided for account registration and login. Message and data rates may apply. Message frequency varies.
            </label>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed pl-7">
            This consent is separate from the Terms of Service and Privacy Policy. SMS messages from JSN are used only for account verification and security, not for marketing.
          </p>
        </div>

        {/* Password */}
        <div>
          <label className="label-jsn" htmlFor="reg-password">Password</label>
          <p className="text-xs text-muted-foreground mb-1.5">At least 8 characters with a number and special character.</p>
          <div className="relative">
            <input id="reg-password" type={showPassword ? 'text' : 'password'} className="input-jsn pr-10" placeholder="Create a strong password"
              {...register('password', { required: 'Password is required', minLength: { value: 8, message: 'At least 8 characters' }, pattern: { value: /(?=.*[0-9])(?=.*[^A-Za-z0-9])/, message: 'Must include a number and special character' } })}
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Toggle password">
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <FieldWarning message={errors.password.message!} />}
        </div>

        {/* Confirm Password */}
        <div>
          <label className="label-jsn" htmlFor="reg-confirm">Confirm Password</label>
          <div className="relative">
            <input id="reg-confirm" type={showConfirm ? 'text' : 'password'} className="input-jsn pr-10" placeholder="Repeat your password"
              {...register('confirmPassword', { required: 'Please confirm your password', validate: (val) => val === passwordValue || 'Passwords do not match' })}
            />
            <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Toggle confirm password">
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.confirmPassword && <FieldWarning message={errors.confirmPassword.message!} />}
        </div>

        {/* DOB + Gender */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-jsn" htmlFor="reg-dob">Date of Birth <span className="text-primary">*</span></label>
            <input id="reg-dob" type="date" className="input-jsn"
              {...register('dateOfBirth', {
                required: 'Date of birth is required',
                validate: (val) => {
                  const dob = new Date(val);
                  const today = new Date();
                  let age = today.getFullYear() - dob.getFullYear();
                  const m = today.getMonth() - dob.getMonth();
                  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
                  if (age < 21) return 'You must be at least 21 years old to use Jamuhuri Singles Network.';
                  return true;
                }
              })}
            />
            {errors.dateOfBirth && <FieldWarning message={errors.dateOfBirth.message!} />}
          </div>
          <div ref={errors.gender ? (el) => { if (!firstErrorRef.current) firstErrorRef.current = el; } : undefined}>
            <label className="label-jsn" htmlFor="reg-gender">Gender <span className="text-primary">*</span></label>
            <select id="reg-gender" className={selectClass} {...register('gender', { required: 'Please select your gender.' })}>
              <option value="">Select</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
            {errors.gender && <FieldWarning message={errors.gender.message!} />}
          </div>
        </div>

        {/* US State */}
        <div>
          <label className="label-jsn" htmlFor="reg-us-state">Current U.S. State / Location</label>
          <select id="reg-us-state" className={selectClass} {...register('usState', { required: 'Please select your US state' })}>
            <option value="">Select your state</option>
            {US_STATES.map(state => <option key={`state-${state}`} value={state}>{state}</option>)}
          </select>
          {errors.usState && <FieldWarning message={errors.usState.message!} />}
        </div>

        {/* Kenyan County */}
        <div>
          <label className="label-jsn" htmlFor="reg-county">Kenyan County / Background</label>
          <p className="text-xs text-muted-foreground mb-1.5">Where you, your family, or your cultural roots connect to in Kenya.</p>
          <select id="reg-county" className={selectClass} {...register('kenyanCounty', { required: 'Please select your Kenyan county' })}>
            <option value="">Select county</option>
            {KENYAN_COUNTIES.map(county => <option key={`county-${county}`} value={county}>{county}</option>)}
          </select>
          {errors.kenyanCounty && <FieldWarning message={errors.kenyanCounty.message!} />}
        </div>

        {/* Relationship Intention */}
        <div>
          <label className="label-jsn" htmlFor="reg-intention">Relationship Intention</label>
          <select id="reg-intention" className={selectClass} {...register('intention', { required: 'Please select your relationship intention.' })}>
            <option value="">What are you looking for?</option>
            {RELATIONSHIP_INTENTIONS.map(i => <option key={`int-${i}`} value={i}>{i}</option>)}
          </select>
          {errors.intention && <FieldWarning message={errors.intention.message!} />}
        </div>

        {/* Faith */}
        <div>
          <label className="label-jsn" htmlFor="reg-faith">Faith / Values</label>
          <select id="reg-faith" className={selectClass} {...register('faith', { required: 'Please select your faith' })}>
            <option value="">Select faith / values</option>
            {FAITH_OPTIONS.map(f => <option key={`faith-${f}`} value={f}>{f}</option>)}
          </select>
          {errors.faith && <FieldWarning message={errors.faith.message!} />}
        </div>

        {/* Interests */}
        <div ref={interestsSectionRef}>
          <label className="label-jsn">Interests <span className="text-primary">*</span></label>
          <p className="text-xs text-muted-foreground mb-1.5">Select at least one interest.</p>
          <div className={`flex flex-wrap gap-2 mt-1 p-2 rounded-xl border transition-colors ${interestsError ? 'border-amber-500 bg-amber-500/5' : 'border-transparent'}`}>
            {INTERESTS_LIST.map(interest => (
              <button
                key={`interest-${interest}`}
                type="button"
                onClick={() => toggleInterest(interest)}
                className={`filter-chip text-xs ${selectedInterests.includes(interest) ? 'filter-chip-active' : ''}`}
              >
                {selectedInterests.includes(interest) && <Check size={10} className="inline mr-1" />}
                {interest}
              </button>
            ))}
          </div>
          {interestsError && <FieldWarning message={interestsError} />}
        </div>

        {/* Bio */}
        <div>
          <label className="label-jsn" htmlFor="reg-bio">Short Bio <span className="text-primary">*</span></label>
          <p className="text-xs text-muted-foreground mb-1.5">Tell others about yourself in a few sentences.</p>
          <textarea
            id="reg-bio"
            rows={3}
            maxLength={500}
            className={`input-jsn resize-none ${errors.bio ? 'border-amber-500 bg-amber-500/5' : ''}`}
            placeholder="Share a bit about yourself, your background, and what you're looking for..."
            {...register('bio', {
              required: 'Please write a short bio',
              minLength: { value: 20, message: 'Bio must be at least 20 characters.' },
              maxLength: { value: 500, message: 'Bio cannot exceed 500 characters.' },
              onChange: (e) => setBioLength(e.target.value.length),
            })}
          />
          <div className="flex items-center justify-between mt-1">
            {errors.bio ? (
              <FieldWarning message={errors.bio.message!} />
            ) : (
              <span />
            )}
            <span className={`text-xs ml-auto ${bioLength > 500 ? 'text-red-500 font-semibold' : bioLength > 450 ? 'text-amber-500' : 'text-muted-foreground'}`}>
              {bioLength} / 500
            </span>
          </div>
        </div>

        {/* Terms */}
        <div className="flex items-start gap-2.5">
          <input
            id="agree-terms"
            type="checkbox"
            className="w-4 h-4 mt-0.5 rounded border-border accent-primary cursor-pointer flex-shrink-0"
            {...register('agreeTerms', { required: 'You must agree to the terms to continue' })}
          />
          <label htmlFor="agree-terms" className="text-sm text-muted-foreground cursor-pointer leading-relaxed">
            I agree to the{' '}
            <a href="/safety" className="text-primary hover:underline">Terms of Service</a>
            {' '}and{' '}
            <a href="/safety" className="text-primary hover:underline">Privacy Policy</a>.
            I confirm I am 18 years or older.
          </label>
        </div>
        {errors.agreeTerms && <FieldWarning message={errors.agreeTerms.message!} />}

        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          style={{ minHeight: '44px' }}
        >
          {isLoading ? (
            <><Loader2 size={16} className="animate-spin" />Creating your profile...</>
          ) : (
            'Create My JSN Profile'
          )}
        </button>
      </form>

      <div className="text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <button onClick={onSwitchToLogin} className="text-primary hover:text-primary-light font-semibold transition-colors">
          Sign In
        </button>
      </div>
    </div>
  );
}